"use client"

import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface DateRange {
  from?: Date
  to?: Date
}

interface DateRangePickerProps {
  dateRange?: DateRange
  onDateRangeChange?: (range: DateRange | undefined) => void
  placeholder?: string
  className?: string
  align?: "start" | "center" | "end"
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  placeholder = "Pick a date range",
  className,
  align = "start"
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const formatDateRange = (range?: DateRange) => {
    if (!range?.from) return placeholder
    if (!range.to) return `${range.from.toLocaleDateString()}`
    return `${range.from.toLocaleDateString()} - ${range.to.toLocaleDateString()}`
  }

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFromDate = e.target.value ? new Date(e.target.value) : undefined
    onDateRangeChange?.({
      from: newFromDate,
      to: dateRange?.to
    })
  }

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newToDate = e.target.value ? new Date(e.target.value) : undefined
    onDateRangeChange?.({
      from: dateRange?.from,
      to: newToDate
    })
  }

  const clearDates = () => {
    onDateRangeChange?.(undefined)
    setIsOpen(false)
  }

  const applyDates = () => {
    setIsOpen(false)
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date-range"
            variant="outline"
            className={cn(
              "justify-start text-left font-normal"
            )}
          >
            <CalendarIcon className="h-4 w-4 text-gray-800" />
            {formatDateRange(dateRange)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-4" align={align}>
          <div className="space-y-4">
            <div className="text-sm font-medium">Select Date Range</div>
            <div className="grid gap-3">
              <div className="grid gap-2">
                <label className="text-sm font-medium">From Date</label>
                <input
                  type="date"
                  value={dateRange?.from ? dateRange.from.toISOString().split('T')[0] : ''}
                  onChange={handleFromDateChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">To Date</label>
                <input
                  type="date"
                  value={dateRange?.to ? dateRange.to.toISOString().split('T')[0] : ''}
                  onChange={handleToDateChange}
                  min={dateRange?.from ? dateRange.from.toISOString().split('T')[0] : undefined}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={clearDates}
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={applyDates}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
